import * as THREE from 'three';
import {
  isDefaultVenueHeaderFlag,
  loadVenueHeaderFlagTexture,
  VENUE_HEADER_FLAG_TEXTURE_CHANGED_EVENT,
} from './venueHeaderFlagTexture.js';
import {
  createAnimatedFlagMesh,
  FLAG_ASPECT,
  setFlagMeshWaveStrength,
  updateFlagMeshAnimation,
} from './startMarker.js';

/**
 * @typedef {object} AnimatedFlagProfile
 * @property {string} canvasClassName
 * @property {number} [fixedHeightPx]
 * @property {number} waveStrengthMult
 * @property {number} animTimeScale
 * @property {{ x: number; y: number }} waveFrequency
 * @property {number} glowBoost
 * @property {number} brightness
 * @property {number} maxDisplacement
 * @property {number} verticalScale
 * @property {number} maxVertical
 * @property {number} segmentsX
 * @property {number} segmentsY
 * @property {number} viewH
 * @property {number} [maxDpr]
 */

/** @type {AnimatedFlagProfile} */
const HEADER_PROFILE = {
  canvasClassName: 'venue-header__flag-canvas',
  waveStrengthMult: 1.35,
  animTimeScale: 1.2,
  waveFrequency: { x: 3.6, y: 4.4 },
  glowBoost: 0.78,
  brightness: 1.28,
  maxDisplacement: 0.09,
  verticalScale: 0.2,
  maxVertical: 0.024,
  segmentsX: 40,
  segmentsY: 22,
  viewH: 0.26,
  maxDpr: 2,
};

/** @type {AnimatedFlagProfile} */
const MARKERS_PANEL_PROFILE = {
  canvasClassName: 'markers-panel-card__flag-canvas',
  fixedHeightPx: 16,
  waveStrengthMult: 1.35,
  animTimeScale: 1.2,
  waveFrequency: { x: 3.6, y: 4.4 },
  glowBoost: 0.78,
  brightness: 1.28,
  maxDisplacement: 0.09,
  verticalScale: 0.2,
  maxVertical: 0.024,
  segmentsX: 28,
  segmentsY: 16,
  viewH: 0.26,
  maxDpr: 2,
};

/**
 * @param {HTMLElement} container
 * @param {AnimatedFlagProfile} profile
 */
function mountAnimatedFlag(container, profile) {
  const canvas = document.createElement('canvas');
  canvas.className = profile.canvasClassName;
  container.replaceChildren(canvas);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    powerPreference: 'low-power',
  });
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  /** @type {THREE.OrthographicCamera | null} */
  let camera = null;
  /** @type {THREE.Mesh | null} */
  let flagMesh = null;
  let baseWaveStrength = 0.095;
  let disposed = false;

  function effectiveWaveStrength() {
    return baseWaveStrength * profile.waveStrengthMult;
  }

  function resize() {
    let cssW;
    let cssH;

    if (profile.fixedHeightPx) {
      cssH = profile.fixedHeightPx;
      cssW = cssH * FLAG_ASPECT;
      container.style.width = `${cssW}px`;
      container.style.height = `${cssH}px`;
    } else {
      const rect = container.getBoundingClientRect();
      cssW = Math.max(1, rect.width);
      cssH = Math.max(1, rect.height || cssW / FLAG_ASPECT);
    }

    const dpr = Math.min(window.devicePixelRatio || 1, profile.maxDpr ?? 2);
    renderer.setPixelRatio(dpr);
    renderer.setSize(cssW, cssH, false);

    const viewH = profile.viewH;
    const viewW = viewH * (cssW / cssH);
    camera = new THREE.OrthographicCamera(
      -viewW / 2,
      viewW / 2,
      viewH / 2,
      -viewH / 2,
      0.1,
      10,
    );
    camera.position.z = 2;
    camera.lookAt(0, 0, 0);
  }

  function disposeFlagMesh() {
    if (!flagMesh) return;
    scene.remove(flagMesh);
    flagMesh.geometry.dispose();
    flagMesh.material.dispose();
    flagMesh = null;
  }

  async function rebuildFlagMesh() {
    if (disposed) return;
    try {
      const texture = await loadVenueHeaderFlagTexture();
      if (disposed) return;
      disposeFlagMesh();
      flagMesh = createAnimatedFlagMesh(texture, {
        attachToPole: false,
        segmentsX: profile.segmentsX,
        segmentsY: profile.segmentsY,
        strength: effectiveWaveStrength(),
        frequency: profile.waveFrequency,
        checkeredCutout: isDefaultVenueHeaderFlag(),
        glowBoost: profile.glowBoost,
        brightness: profile.brightness,
        maxDisplacement: profile.maxDisplacement,
        verticalScale: profile.verticalScale,
        maxVertical: profile.maxVertical,
      });
      scene.add(flagMesh);
      resize();
    } catch (err) {
      console.error('Failed to load animated flag:', err);
    }
  }

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(container);
  resize();
  rebuildFlagMesh();

  const onFlagTextureChanged = () => {
    rebuildFlagMesh();
  };
  window.addEventListener(
    VENUE_HEADER_FLAG_TEXTURE_CHANGED_EVENT,
    onFlagTextureChanged,
  );

  return {
    /**
     * @param {number} elapsedSeconds
     * @param {number} [animationSpeed]
     */
    tick(elapsedSeconds, animationSpeed = 1) {
      if (!flagMesh || !camera) return;
      updateFlagMeshAnimation(
        flagMesh,
        elapsedSeconds,
        animationSpeed,
        profile.animTimeScale,
      );
      renderer.render(scene, camera);
    },
    /**
     * @param {number} strength
     */
    setWaveStrength(strength) {
      baseWaveStrength = strength;
      if (flagMesh) setFlagMeshWaveStrength(flagMesh, effectiveWaveStrength());
    },
    dispose() {
      disposed = true;
      window.removeEventListener(
        VENUE_HEADER_FLAG_TEXTURE_CHANGED_EVENT,
        onFlagTextureChanged,
      );
      resizeObserver.disconnect();
      disposeFlagMesh();
      renderer.dispose();
    },
  };
}

/**
 * @param {HTMLElement} container
 */
export function mountVenueHeaderFlag(container) {
  return mountAnimatedFlag(container, HEADER_PROFILE);
}

/**
 * @param {HTMLElement} container
 */
export function mountMarkersPanelFlag(container) {
  return mountAnimatedFlag(container, MARKERS_PANEL_PROFILE);
}
